/**
 * Check this team (packages/shared/src/schemas/teamCheck.ts): the findings as plain rows, a dot and a word
 * beside each sentence, every fix one button, and "Fix all I can" when several apply. A repair answers with the
 * check made after it, so the rows are always the platform's latest. Folding a duplicate team in removes a
 * team, so it asks first and is never part of "Fix all I can".
 */

import type { TeamCheck, TeamCheckFinding, TeamCheckSeverity, TeamRepairAction, TeamRepairAnswer } from '@aioe/shared';
import { useState } from 'react';

import { problemText, routes } from '../../api/client.ts';
import { timeAgo, useLoad } from '../../hooks.ts';
import { Button, ConfirmDialog, ErrorNote, StatusMark } from '../ui.tsx';
import { TEAM_ATTENTION_CHANGED } from './teamsModel.ts';

const SEVERITY: Record<TeamCheckSeverity, { state: 'error' | 'medium' | 'idle'; word: string }> = {
  error: { state: 'error', word: 'Needs fixing' },
  warning: { state: 'medium', word: 'Worth a look' },
  info: { state: 'idle', word: 'Note' },
};

/** The quiet mark a team that needs attention carries in a list; every other team carries nothing. */
export function NeedsAttention({ errors, warnings }: { errors: number; warnings: number }) {
  if (!errors && !warnings) return null;
  const n = errors || warnings;
  return <StatusMark state={errors ? 'error' : 'medium'} label={`Needs attention${n > 1 ? ` (${n})` : ''}`} />;
}

const checkedWhen = (iso: string) => (Date.now() - Date.parse(iso) < 10_000 ? 'just now' : timeAgo(iso));
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
const sameAction = (a: TeamRepairAction, b: TeamRepairAction) => JSON.stringify(a) === JSON.stringify(b);

function FindingRow({ f, busy, onFix }: { f: TeamCheckFinding; busy: boolean; onFix: (a: TeamRepairAction) => void }) {
  const s = SEVERITY[f.severity];
  return (
    <li className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1.5">
      <p className="flex min-w-0 flex-1 basis-72 items-start gap-2">
        <span className="status-dot mt-1.5 shrink-0" data-state={s.state} aria-hidden="true" />
        <span>
          <span className="font-medium">{s.word}.</span> {f.words}
        </span>
      </p>
      {f.fix ? (
        <Button size="sm" variant="secondary" isDisabled={busy} onPress={() => onFix(f.fix!.action)}>
          {f.fix.label}
        </Button>
      ) : null}
    </li>
  );
}

/**
 * The results of checking one team, loaded when shown. `projectId`: the project the person is looking from, so
 * a team not granted to it says so. `onRepaired`: the page reloads what it shows of the team.
 */
export function TeamCheckResults({ teamId, kind, projectId, onClose, onRepaired }: { teamId: string; kind?: 'team' | 'specialist'; projectId?: string; onClose: () => void; onRepaired?: () => void }) {
  // The check as loaded, until a repair answers with the one made after it.
  const loaded = useLoad(() => routes.teamCheck(teamId, projectId), [teamId, projectId]);
  const [after, setAfter] = useState<TeamCheck>();
  const check = after ?? loaded.data;
  const [last, setLast] = useState<TeamRepairAnswer>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [confirm, setConfirm] = useState<TeamRepairAction>();
  const word = kind === 'specialist' ? 'specialist' : 'team';

  const load = () => {
    setAfter(undefined);
    setLast(undefined);
    setError(undefined);
    loaded.reload();
  };

  const fix = async (actions: TeamRepairAction[]) => {
    setBusy(true);
    setError(undefined);
    try {
      const answer = await routes.repairTeam(teamId, actions, projectId);
      setLast(answer);
      setAfter(answer.check);
      if (answer.applied.length) {
        onRepaired?.();
        window.dispatchEvent(new Event(TEAM_ATTENTION_CHANGED));
      }
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  const onFix = (a: TeamRepairAction) => (a.kind === 'merge-duplicate' ? setConfirm(a) : void fix([a]));

  const fixes = (check?.findings ?? []).flatMap((f) => (f.fix && f.fix.action.kind !== 'merge-duplicate' ? [f.fix.action] : [])).filter((a, i, all) => all.findIndex((b) => sameAction(a, b)) === i);
  const errors = (check?.findings ?? []).filter((f) => f.severity === 'error').length;
  const warnings = (check?.findings ?? []).filter((f) => f.severity === 'warning').length;
  const summary = check ? (check.findings.length ? [errors ? plural(errors, 'thing to fix', 'things to fix') : '', warnings ? plural(warnings, 'thing worth a look', 'things worth a look') : ''].filter(Boolean).join(', ') || plural(check.findings.length, 'note', 'notes') : '') : '';

  return (
    <div className="grid gap-3 rounded-lg bg-surface-secondary p-3 text-sm" aria-live="polite" data-team-check={teamId}>
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <p className="text-xs text-muted">{check ? `Checked ${checkedWhen(check.checkedAt)}${summary ? ` · ${summary}` : ''}` : loaded.error ? '' : 'Checking…'}</p>
        <div className="flex flex-wrap items-center gap-2">
          {fixes.length > 1 ? (
            <Button size="sm" variant="primary" isPending={busy} onPress={() => void fix(fixes)}>
              Fix all I can
            </Button>
          ) : null}
          <Button size="sm" variant="tertiary" isDisabled={busy} onPress={load}>
            Check again
          </Button>
          <Button size="sm" variant="tertiary" onPress={onClose}>
            Hide
          </Button>
        </div>
      </div>
      <ErrorNote error={error ?? loaded.error} />
      {last && (last.applied.length || last.refused.length) ? (
        <ul className="grid gap-1 text-xs">
          {last.applied.map((o, i) => (
            <li key={`a${i}`} className="flex items-start gap-2">
              <span className="status-dot mt-1 shrink-0" data-state="connected" aria-hidden="true" />
              <span>
                <span className="font-medium">Fixed.</span> {o.words}
              </span>
            </li>
          ))}
          {last.refused.map((o, i) => (
            <li key={`r${i}`} className="flex items-start gap-2">
              <span className="status-dot mt-1 shrink-0" data-state={o.reason === 'not_needed' ? 'idle' : 'medium'} aria-hidden="true" />
              <span>
                <span className="font-medium">{o.reason === 'not_needed' ? 'Already right.' : 'Not fixed.'}</span> {o.words}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {check ? (
        check.findings.length ? (
          <ul className="grid gap-2.5">
            {check.findings.map((f) => (
              <FindingRow key={f.id} f={f} busy={busy} onFix={onFix} />
            ))}
          </ul>
        ) : (
          <StatusMark state="connected" label={`Nothing wrong with this ${word}.`} />
        )
      ) : null}
      {confirm && confirm.kind === 'merge-duplicate' ? (
        <ConfirmDialog
          isOpen
          title={`Fold ${confirm.from} into this ${word}?`}
          confirmLabel="Fold it in and remove it"
          danger
          onCancel={() => setConfirm(undefined)}
          onConfirm={async () => {
            const a = confirm;
            setConfirm(undefined);
            await fix([a]);
          }}
        >
          <p>The projects that use {confirm.from} will use this {word} instead, and teams that name it as a specialist will name this one. Then {confirm.from} is removed from your organisation.</p>
        </ConfirmDialog>
      ) : null}
    </div>
  );
}
